import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { DatePipe, DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localePt from '@angular/common/locales/pt';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');
registerLocaleData(localePt, 'pt');
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import {
  provideAuth,
  LogLevel,
  authInterceptor,
  AbstractSecurityStorage,
  DefaultLocalStorageService,
} from 'angular-auth-oidc-client';
import { environment } from '@env/environment';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from '@core/i18n/multi-translate-loader';
import { httpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideMatomo, withRouter as withMatomoRouter } from 'ngx-matomo-client';

function HttpLoaderFactory() {
  return new MultiTranslateHttpLoader([
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/auth/', suffix: '.json' },
    { prefix: './assets/i18n/call-management/', suffix: '.json' },
    { prefix: './assets/i18n/interactions/', suffix: '.json' },
    { prefix: './assets/i18n/locations/', suffix: '.json' },
    { prefix: './assets/i18n/location-map/', suffix: '.json' },
    { prefix: './assets/i18n/paygo/', suffix: '.json' },
    { prefix: './assets/i18n/atlas-admin/', suffix: '.json' },
    { prefix: './assets/i18n/home/', suffix: '.json' },
    { prefix: './assets/i18n/inventory/', suffix: '.json' },
  ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
        },
      }),
      NgbModule,
    ),
    provideAuth({
      config: {
        authority: environment.KEYCLOAK_URL,
        redirectUrl: environment.REDIRECT_URL,
        postLogoutRedirectUri: environment.REDIRECT_URL,
        clientId: environment.KEYCLOAK_CLIENTID,
        scope: 'openid profile email offline_access',
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Debug,
        triggerAuthorizationResultEvent: true,
        secureRoutes: [environment.baseUrl],
      },
    }),
    { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
    provideBrowserGlobalErrorListeners(),
    // `<html lang>` suit la langue de l'app : les lecteurs d'écran prononcent le texte dans la
    // bonne langue, et les grilles ag-Grid y lisent la leur (cf. `grid-locale.ts`).
    provideAppInitializer(() => {
      const document = inject(DOCUMENT);
      inject(TranslateService)
        .onLangChange.pipe(takeUntilDestroyed())
        .subscribe(({ lang }) => (document.documentElement.lang = lang));
    }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor(), httpErrorInterceptor])),
    provideMatomo(
      { trackerUrl: environment.MATOMO_URL_BASE, siteId: environment.MATOMO_SITE_ID },
      withMatomoRouter({ exclude: ['/login'] }),
    ),
    DatePipe,
  ],
};
